import { useState, useEffect, useCallback } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete from '@mui/material/Autocomplete';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LanguageIcon from '@mui/icons-material/Language';
import VerifiedIcon from '@mui/icons-material/Verified';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import ShareIcon from '@mui/icons-material/Share';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
    getIssuers, createIssuer, updateIssuer, deleteIssuer,
    getIssuerStats, getIssuerTeam, inviteToTeam, removeFromTeam,
    getIssuerPartners, requestSharing, breakSharing, getAllIssuers,
    getApiKeys, rotateApiKey, addApiOrigin, removeApiOrigin,
    getWebhooks, addWebhook, removeWebhook,
    IssuerStats, TeamMember, SharingPartner, ApiKeyInfo, IssuerWebhook, PublicIssuer,
} from '../api/issuers';
import { Issuer } from '@tinyrewards/shared';
import SimpleHtmlEditor from '../components/SimpleHtmlEditor';
import { PURPLE_AVATAR } from '../theme/tokens';

// ── Types ─────────────────────────────────────────────────────────────────────

type DialogMode = 'create' | 'edit';
type ActiveModal = 'team' | 'shares' | 'api' | null;

// ── Team Dialog ───────────────────────────────────────────────────────────────

function TeamDialog({ issuer, open, onClose }: { issuer: Issuer; open: boolean; onClose: () => void }) {
    const [members, setMembers] = useState<TeamMember[]>([]);
    const [loading, setLoading] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviting, setInviting] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        getIssuerTeam(issuer.issuerID)
            .then(res => { if (res.success) setMembers(res.data ?? []); })
            .finally(() => setLoading(false));
    }, [issuer.issuerID]);

    useEffect(() => { if (open) { load(); setInviteEmail(''); setMessage(''); setError(''); } }, [open, load]);

    const handleInvite = async () => {
        if (!inviteEmail.trim()) return;
        setInviting(true);
        setError('');
        const res = await inviteToTeam(issuer.issuerID, inviteEmail.trim());
        setInviting(false);
        if (res.success) {
            setMessage('Invitation sent.');
            setInviteEmail('');
            load();
        } else {
            setError(res.message ?? 'Failed to invite.');
        }
    };

    const handleRemove = async (userId: string) => {
        const res = await removeFromTeam(issuer.issuerID, userId);
        if (res.success) load();
        else setError(res.message ?? 'Failed to remove.');
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Team — {issuer.issuerName}</DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

                <Box display="flex" gap={1} mb={2}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Email address to invite"
                        value={inviteEmail}
                        onChange={e => setInviteEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleInvite()}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><PersonAddIcon fontSize="small" /></InputAdornment>,
                        }}
                    />
                    <Button onClick={handleInvite} disabled={inviting || !inviteEmail.trim()}>
                        {inviting ? <CircularProgress size={16} color="inherit" /> : 'Invite'}
                    </Button>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
                ) : members.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" py={2}>No team members yet.</Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Email</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {members.map(m => (
                                <TableRow key={m.UserID}>
                                    <TableCell>{m.Email}</TableCell>
                                    <TableCell>{[m.FirstName, m.LastName].filter(Boolean).join(' ') || '—'}</TableCell>
                                    <TableCell>
                                        <Chip label={m.Status} size="small"
                                            color={m.Status === 'Approved' ? 'success' : m.Status === 'Declined' ? 'error' : 'default'} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Remove">
                                            <IconButton size="small" color="error" onClick={() => handleRemove(m.UserID)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>
            <DialogActions>
                <Button color="secondary" onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Shares Dialog ─────────────────────────────────────────────────────────────

function SharesDialog({ issuer, open, onClose }: { issuer: Issuer; open: boolean; onClose: () => void }) {
    const [tab, setTab] = useState(0);
    const [sharedTo, setSharedTo] = useState<SharingPartner[]>([]);
    const [sharedFrom, setSharedFrom] = useState<SharingPartner[]>([]);
    const [allIssuers, setAllIssuers] = useState<PublicIssuer[]>([]);
    const [target, setTarget] = useState<PublicIssuer | null>(null);
    const [loading, setLoading] = useState(false);
    const [requesting, setRequesting] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            getIssuerPartners(issuer.issuerID),
            getAllIssuers(),
        ]).then(([partners, all]) => {
            if (partners.success && partners.data) {
                setSharedTo(partners.data.issuersSharedTo);
                setSharedFrom(partners.data.issuersSharedFrom);
            }
            if (all.success && all.data) {
                setAllIssuers(all.data.filter(i => i.IssuerID !== issuer.issuerID));
            }
        }).finally(() => setLoading(false));
    }, [issuer.issuerID]);

    useEffect(() => { if (open) { load(); setTarget(null); setMessage(''); setError(''); setTab(0); } }, [open, load]);

    const handleRequest = async () => {
        if (!target) return;
        setRequesting(true);
        setError('');
        const res = await requestSharing(issuer.issuerID, target.IssuerID);
        setRequesting(false);
        if (res.success) {
            setMessage('Sharing request sent.');
            setTarget(null);
            load();
        } else {
            setError(res.message ?? 'Failed to send request.');
        }
    };

    const handleBreak = async (sharingId: string) => {
        const res = await breakSharing(sharingId);
        if (res.success) load();
        else setError(res.message ?? 'Failed to remove sharing.');
    };

    const statusColor = (s: string | null) => s === 'Approved' ? 'success' : s === 'Declined' ? 'error' : 'default';

    const PartnerTable = ({ rows, direction }: { rows: SharingPartner[]; direction: 'to' | 'from' }) => (
        rows.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={2}>
                {direction === 'to' ? 'No outgoing shares.' : 'No incoming shares.'}
            </Typography>
        ) : (
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Issuer</TableCell>
                        <TableCell>Status</TableCell>
                        {direction === 'to' && <TableCell />}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(p => (
                        <TableRow key={p.ID}>
                            <TableCell>
                                <Typography variant="body2">{p.IssuerName}</Typography>
                                {p.IssuerDomain && <Typography variant="caption" color="text.secondary">{p.IssuerDomain}</Typography>}
                            </TableCell>
                            <TableCell><Chip label={p.status ?? 'Pending'} size="small" color={statusColor(p.status) as 'success' | 'error' | 'default'} /></TableCell>
                            {direction === 'to' && (
                                <TableCell align="right">
                                    <Tooltip title="Break sharing">
                                        <IconButton size="small" color="error" onClick={() => handleBreak(p.ID)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        )
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Shares — {issuer.issuerName}</DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

                <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>Share with another issuer</Typography>
                    <Box display="flex" gap={1}>
                        <Autocomplete
                            size="small"
                            fullWidth
                            options={allIssuers}
                            getOptionLabel={o => o.Name + (o.HostDomain ? ` (${o.HostDomain})` : '')}
                            value={target}
                            onChange={(_, v) => setTarget(v)}
                            renderInput={params => <TextField {...params} placeholder="Search issuers…" />}
                        />
                        <Button onClick={handleRequest} disabled={!target || requesting}>
                            {requesting ? <CircularProgress size={16} color="inherit" /> : 'Share'}
                        </Button>
                    </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {loading ? (
                    <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
                ) : (
                    <>
                        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
                            <Tab label={`Shared To (${sharedTo.length})`} />
                            <Tab label={`Shared From (${sharedFrom.length})`} />
                        </Tabs>
                        {tab === 0 && <PartnerTable rows={sharedTo} direction="to" />}
                        {tab === 1 && <PartnerTable rows={sharedFrom} direction="from" />}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button color="secondary" onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

// ── API Keys Dialog ───────────────────────────────────────────────────────────

function ApiKeysDialog({ issuer, open, onClose }: { issuer: Issuer; open: boolean; onClose: () => void }) {
    const [keys, setKeys] = useState<ApiKeyInfo | null>(null);
    const [webhooks, setWebhooks] = useState<IssuerWebhook[]>([]);
    const [loading, setLoading] = useState(false);
    const [newOriginUrl, setNewOriginUrl] = useState('');
    const [newWebhookUrl, setNewWebhookUrl] = useState('');
    const [addingOrigin, setAddingOrigin] = useState(false);
    const [addingWebhook, setAddingWebhook] = useState(false);
    const [generating, setGenerating] = useState<'primary' | 'secondary' | null>(null);
    const [copied, setCopied] = useState('');
    const [error, setError] = useState('');
    const theme = useTheme();

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            getApiKeys(issuer.issuerID),
            getWebhooks(issuer.issuerID),
        ]).then(([keysRes, hooksRes]) => {
            if (keysRes.success && keysRes.data) setKeys(keysRes.data);
            if (hooksRes.success && hooksRes.data) setWebhooks(hooksRes.data);
        }).finally(() => setLoading(false));
    }, [issuer.issuerID]);

    useEffect(() => {
        if (open) { load(); setNewOriginUrl(''); setNewWebhookUrl(''); setError(''); }
    }, [open, load]);

    const copy = (text: string, label: string) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopied(label);
        setTimeout(() => setCopied(''), 2000);
    };

    const handleGenerate = async (keyType: 'primary' | 'secondary') => {
        setGenerating(keyType);
        setError('');
        const res = await rotateApiKey(issuer.issuerID, keyType);
        setGenerating(null);
        if (res.success) load();
        else setError(res.message ?? 'Failed to generate key.');
    };

    const handleAddOrigin = async () => {
        const url = newOriginUrl.trim();
        if (!url) return;
        setAddingOrigin(true);
        setError('');
        const full = url.startsWith('http') ? url : `https://${url}`;
        const res = await addApiOrigin(issuer.issuerID, full);
        setAddingOrigin(false);
        if (res.success) { setNewOriginUrl(''); load(); }
        else setError(res.message ?? 'Failed to add origin.');
    };

    const handleRemoveOrigin = async (originId: string) => {
        const res = await removeApiOrigin(issuer.issuerID, originId);
        if (res.success) load();
        else setError(res.message ?? 'Failed to remove origin.');
    };

    const handleAddWebhook = async () => {
        const url = newWebhookUrl.trim();
        if (!url) return;
        setAddingWebhook(true);
        setError('');
        const full = url.startsWith('http') ? url : `https://${url}`;
        const res = await addWebhook(issuer.issuerID, full);
        setAddingWebhook(false);
        if (res.success) { setNewWebhookUrl(''); load(); }
        else setError(res.message ?? 'Failed to add webhook.');
    };

    const handleRemoveWebhook = async (webhookId: string) => {
        const res = await removeWebhook(issuer.issuerID, webhookId);
        if (res.success) load();
        else setError(res.message ?? 'Failed to remove webhook.');
    };

    const badgeCreationUrl = keys?.issuerUniqueURLCode
        ? `${window.location.origin}/auth/external?code=${keys.issuerUniqueURLCode}`
        : '';
    const swaggerUrl = `${window.location.origin}/swagger`;

    const CopyBtn = ({ text, label }: { text: string; label: string }) => (
        <Tooltip title={copied === label ? 'Copied!' : 'Copy'}>
            <span>
                <IconButton size="small" onClick={() => copy(text, label)} disabled={!text}>
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>API settings for {issuer.issuerName}</DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {loading ? (
                    <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
                ) : (
                    <>
                        {/* Primary Key */}
                        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                            <TextField
                                size="small"
                                fullWidth
                                label="Primary"
                                placeholder="Empty"
                                value={keys?.primaryKey ?? ''}
                                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                            />
                            <Button
                                sx={{ whiteSpace: 'nowrap' }}
                                onClick={() => handleGenerate('primary')}
                                disabled={generating !== null}
                            >
                                {generating === 'primary' ? <CircularProgress size={14} /> : (keys?.primaryKey ? 'Regenerate Key' : 'Generate Key')}
                            </Button>
                            <CopyBtn text={keys?.primaryKey ?? ''} label="primary" />
                        </Box>

                        {/* Secondary Key */}
                        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                            <TextField
                                size="small"
                                fullWidth
                                label="Secondary"
                                placeholder="Empty"
                                value={keys?.secondaryKey ?? ''}
                                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                            />
                            <Button
                                sx={{ whiteSpace: 'nowrap' }}
                                onClick={() => handleGenerate('secondary')}
                                disabled={generating !== null}
                            >
                                {generating === 'secondary' ? <CircularProgress size={14} /> : (keys?.secondaryKey ? 'Regenerate Key' : 'Generate Key')}
                            </Button>
                            <CopyBtn text={keys?.secondaryKey ?? ''} label="secondary" />
                        </Box>

                        {/* Issuer ID */}
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <TextField
                                size="small"
                                fullWidth
                                label="Issuer ID"
                                value={issuer.issuerID}
                                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                            />
                            <CopyBtn text={issuer.issuerID} label="issuerID" />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* API links */}
                        <Typography variant="subtitle2" gutterBottom>API</Typography>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Typography variant="body2">
                                <a href={swaggerUrl} target="_blank" rel="noreferrer" style={{ color: theme.palette.accent.main }}>
                                    Badge Management
                                </a>
                            </Typography>
                            <CopyBtn text={swaggerUrl} label="swagger" />
                        </Box>

                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                            Access Badge Creation via the API User:
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            {badgeCreationUrl ? (
                                <Typography variant="body2">
                                    <a href={badgeCreationUrl} target="_blank" rel="noreferrer" style={{ color: theme.palette.accent.main }}>
                                        API Access link
                                    </a>
                                </Typography>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Generate a key to get an API Access link.
                                </Typography>
                            )}
                            <CopyBtn text={badgeCreationUrl} label="accessLink" />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Whitelist Origins */}
                        <Typography variant="subtitle2" gutterBottom>White List Origins</Typography>
                        <Box display="flex" gap={1} mb={1}>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="www.example.com"
                                value={newOriginUrl}
                                onChange={e => setNewOriginUrl(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleAddOrigin()}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">https://</InputAdornment>,
                                }}
                            />
                            <Button
                                sx={{ whiteSpace: 'nowrap' }}
                                onClick={handleAddOrigin}
                                disabled={addingOrigin || !newOriginUrl.trim()}
                            >
                                {addingOrigin ? <CircularProgress size={14} /> : 'Add Origin'}
                            </Button>
                        </Box>
                        {keys?.whiteListedOrigins.map(o => (
                            <Box key={o.ID} display="flex" justifyContent="space-between" alignItems="center" mb={0.5}
                                sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1.5, py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                    https://{o.URL}
                                </Typography>
                                <IconButton size="small" color="error" onClick={() => handleRemoveOrigin(o.ID)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                        {(keys?.whiteListedOrigins.length ?? 0) === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                No origins configured — all origins are allowed.
                            </Typography>
                        )}

                        <Divider sx={{ my: 2 }} />

                        {/* Webhooks */}
                        <Typography variant="subtitle2" gutterBottom>Webhook URL</Typography>
                        <Box display="flex" gap={1} mb={1}>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="www.example.com/hook"
                                value={newWebhookUrl}
                                onChange={e => setNewWebhookUrl(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleAddWebhook()}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">https://</InputAdornment>,
                                }}
                            />
                            <Button
                                sx={{ whiteSpace: 'nowrap' }}
                                onClick={handleAddWebhook}
                                disabled={addingWebhook || !newWebhookUrl.trim()}
                            >
                                {addingWebhook ? <CircularProgress size={14} /> : 'Add Webhook'}
                            </Button>
                        </Box>
                        {webhooks.map(w => (
                            <Box key={w.ID} display="flex" justifyContent="space-between" alignItems="center" mb={0.5}
                                sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1.5, py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                    {w.URL}
                                </Typography>
                                <IconButton size="small" color="error" onClick={() => handleRemoveWebhook(w.ID)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                        {webhooks.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                No webhooks configured.
                            </Typography>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button color="secondary" onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function IssuersPage() {
    const [issuers, setIssuers] = useState<Issuer[]>([]);
    const [statsMap, setStatsMap] = useState<Record<string, IssuerStats>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Create / edit dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<DialogMode>('create');
    const [editingIssuer, setEditingIssuer] = useState<Issuer | null>(null);
    const [issuerName, setIssuerName] = useState('');
    const [issuerURL, setIssuerURL] = useState('');
    const [issuerEmail, setIssuerEmail] = useState('');
    const [issuerDescription, setIssuerDescription] = useState('');
    const [formError, setFormError] = useState<string | null>(null);
    const [formSaving, setFormSaving] = useState(false);

    // Delete
    const [deleteDialog, setDeleteDialog] = useState<Issuer | null>(null);
    const [deleting, setDeleting] = useState(false);

    // Sub-modals
    const [activeModal, setActiveModal] = useState<ActiveModal>(null);
    const [modalIssuer, setModalIssuer] = useState<Issuer | null>(null);

    const openModal = (modal: ActiveModal, issuer: Issuer) => {
        setModalIssuer(issuer);
        setActiveModal(modal);
    };

    const loadIssuers = useCallback(() => {
        setLoading(true);
        setError(null);
        getIssuers()
            .then(res => {
                if (res.success && res.data) {
                    const list = res.data;
                    setIssuers(list);
                    // Fetch stats for all issuers in parallel
                    Promise.allSettled(list.map(i => getIssuerStats(i.issuerID)))
                        .then(results => {
                            const map: Record<string, IssuerStats> = {};
                            results.forEach((r, idx) => {
                                if (r.status === 'fulfilled' && r.value.success && r.value.data) {
                                    map[list[idx].issuerID] = r.value.data;
                                }
                            });
                            setStatsMap(map);
                        });
                } else {
                    setError(res.message ?? 'Failed to load issuers.');
                }
            })
            .catch(() => setError('An unexpected error occurred.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { loadIssuers(); }, [loadIssuers]);

    const resetForm = () => {
        setIssuerName(''); setIssuerURL(''); setIssuerEmail(''); setIssuerDescription('');
        setFormError(null); setEditingIssuer(null);
    };

    const openCreate = () => { resetForm(); setDialogMode('create'); setDialogOpen(true); };

    const openEdit = (issuer: Issuer) => {
        setDialogMode('edit');
        setEditingIssuer(issuer);
        setIssuerName(issuer.issuerName);
        setIssuerURL(issuer.issuerURL ?? '');
        setIssuerEmail(issuer.issuerEmail ?? '');
        setIssuerDescription(issuer.issuerDescription ?? '');
        setFormError(null);
        setDialogOpen(true);
    };

    const closeDialog = () => { setDialogOpen(false); resetForm(); };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError(null);
        setFormSaving(true);
        try {
            if (dialogMode === 'create') {
                const res = await createIssuer({ issuerName, issuerURL: issuerURL || undefined, issuerEmail: issuerEmail || undefined, issuerDescription: issuerDescription || undefined });
                if (res.success && res.data) { setIssuers(prev => [...prev, res.data!]); closeDialog(); }
                else setFormError(res.message ?? 'Failed to create issuer.');
            } else if (editingIssuer) {
                const res = await updateIssuer(editingIssuer.issuerID, { issuerName, issuerURL: issuerURL || '', issuerEmail: issuerEmail || '', issuerDescription: issuerDescription || '' });
                if (res.success && res.data) { setIssuers(prev => prev.map(i => i.issuerID === editingIssuer.issuerID ? res.data! : i)); closeDialog(); }
                else setFormError(res.message ?? 'Failed to update issuer.');
            }
        } catch { setFormError('An unexpected error occurred.'); }
        finally { setFormSaving(false); }
    };

    const handleDelete = async () => {
        if (!deleteDialog) return;
        setDeleting(true);
        try {
            const res = await deleteIssuer(deleteDialog.issuerID);
            if (res.success) { setIssuers(prev => prev.filter(i => i.issuerID !== deleteDialog.issuerID)); setDeleteDialog(null); }
            else alert(res.message ?? 'Failed to delete issuer.');
        } catch { alert('An unexpected error occurred.'); }
        finally { setDeleting(false); }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>My Issuers</Typography>
                    {!loading && (
                        <Typography variant="body2" color="text.secondary">
                            {issuers.length} issuer{issuers.length !== 1 ? 's' : ''}
                        </Typography>
                    )}
                </Box>
                <Button startIcon={<AddIcon />} onClick={openCreate}>
                    Add Issuer
                </Button>
            </Box>

            {loading && <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>}
            {!loading && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {!loading && !error && issuers.length === 0 && (
                <Box textAlign="center" py={8} color="text.secondary">
                    <BusinessIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                    <Typography mt={2} mb={2}>You haven't created any issuers yet.</Typography>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                        Issuers represent the organizations or individuals that award badges.
                        Create your first issuer to start designing and issuing badges.
                    </Typography>
                    <Button startIcon={<AddIcon />} onClick={openCreate}>
                        Create Your First Issuer
                    </Button>
                </Box>
            )}

            {!loading && !error && issuers.length > 0 && (
                <Grid container spacing={3}>
                    {issuers.map(issuer => {
                        const stats = statsMap[issuer.issuerID];
                        return (
                            <Grid item xs={12} sm={6} lg={4} key={issuer.issuerID}>
                                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        {/* Header row */}
                                        <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                                            <Box sx={{ bgcolor: PURPLE_AVATAR, borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                {issuer.issuerImage ? (
                                                    <Box component="img" src={issuer.issuerImage} alt={issuer.issuerName} sx={{ width: 32, height: 32, borderRadius: 1 }} />
                                                ) : (
                                                    <BusinessIcon sx={{ fontSize: 32, color: 'accent.main' }} />
                                                )}
                                            </Box>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography variant="h6" fontWeight={700} noWrap>{issuer.issuerName}</Typography>
                                                {issuer.issuerURL && (
                                                    <Typography variant="body2" color="text.secondary" noWrap sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <LanguageIcon sx={{ fontSize: 14 }} />{issuer.issuerURL}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Description */}
                                        {issuer.issuerDescription && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, '& b, & strong': { fontWeight: 700 } }}
                                                dangerouslySetInnerHTML={{
                                                    __html: issuer.issuerDescription.length > 120
                                                        ? issuer.issuerDescription.slice(0, 120) + '&hellip;'
                                                        : issuer.issuerDescription,
                                                }}
                                            />
                                        )}

                                        {/* Status chips */}
                                        <Box display="flex" flexWrap="wrap" gap={0.5} mb={1.5}>
                                            <Chip label={issuer.isActive ? 'Active' : 'Inactive'} color={issuer.isActive ? 'success' : 'default'} size="small" />
                                            {issuer.isVerified && <Chip icon={<VerifiedIcon />} label="Verified" color="info" size="small" />}
                                        </Box>

                                        {/* Badge stats */}
                                        <Box display="flex" gap={2} mb={1}>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <WorkspacePremiumIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    {stats ? stats.badgesCreated : '—'} badge{stats?.badgesCreated !== 1 ? 's' : ''} created
                                                </Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <PersonAddIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    {stats ? stats.badgesIssued : '—'} issued
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Typography variant="caption" color="text.disabled" display="block">
                                            Created {new Date(issuer.dateCreated).toLocaleDateString()}
                                        </Typography>
                                    </CardContent>

                                    <CardActions sx={{ px: 2, pb: 2, gap: 0.5, borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
                                        <ButtonGroup>
                                            <Button startIcon={<EditIcon />} onClick={() => openEdit(issuer)}>Edit</Button>
                                            <Button startIcon={<GroupIcon />} onClick={() => openModal('team', issuer)}>Team</Button>
                                            <Button startIcon={<ShareIcon />} onClick={() => openModal('shares', issuer)}>Shares</Button>
                                            <Button startIcon={<VpnKeyIcon />} onClick={() => openModal('api', issuer)}>API</Button>
                                        </ButtonGroup>
                                        <Box flex={1} />
                                        <Tooltip title="Delete issuer">
                                            <IconButton size="small" color="error" onClick={() => setDeleteDialog(issuer)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </CardActions>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Create / Edit Dialog */}
            <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{dialogMode === 'create' ? 'Add Issuer' : 'Edit Issuer'}</DialogTitle>
                <Box component="form" onSubmit={handleSubmit}>
                    <DialogContent dividers>
                        {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
                        <Box display="flex" flexDirection="column" gap={2}>
                            <TextField label="Issuer Name" value={issuerName} onChange={e => setIssuerName(e.target.value)} required size="small" fullWidth autoFocus />
                            <TextField label="Website URL" type="url" value={issuerURL} onChange={e => setIssuerURL(e.target.value)} placeholder="https://example.com" size="small" fullWidth />
                            <TextField label="Contact Email" type="email" value={issuerEmail} onChange={e => setIssuerEmail(e.target.value)} size="small" fullWidth />
                            <Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>Description</Typography>
                                <SimpleHtmlEditor value={issuerDescription} onChange={setIssuerDescription} placeholder="Describe this issuer..." minHeight={80} />
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button color="secondary" onClick={closeDialog}>Cancel</Button>
                        <Button type="submit" disabled={formSaving}>
                            {formSaving ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
                            {dialogMode === 'create' ? 'Add Issuer' : 'Save Changes'}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete Issuer</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete <strong>{deleteDialog?.issuerName}</strong>?
                        This will deactivate the issuer and all associated badges.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={() => setDeleteDialog(null)}>Cancel</Button>
                    <Button color="error" onClick={handleDelete} disabled={deleting}>
                        {deleting ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Sub-modals */}
            {modalIssuer && (
                <>
                    <TeamDialog issuer={modalIssuer} open={activeModal === 'team'} onClose={() => setActiveModal(null)} />
                    <SharesDialog issuer={modalIssuer} open={activeModal === 'shares'} onClose={() => setActiveModal(null)} />
                    <ApiKeysDialog issuer={modalIssuer} open={activeModal === 'api'} onClose={() => setActiveModal(null)} />
                </>
            )}
        </Container>
    );
}
